import Checkbox, { GroupedCheckboxProps } from '@repo/ui/checkbox'
import { FieldValues } from 'react-hook-form'

import { useFormField } from '../forms/form'

export type GroupedCheckboxInputType<T> = Partial<Record<keyof T, string>>

type ControlledGroupedCheckboxInputProps<T extends FieldValues | undefined> = Omit<
  GroupedCheckboxProps,
  'onChildChange' | 'onParentChange' | 'checkedValues'
> & {
  value?: T
  onChange: (value: T) => void
}

export function ControlledGroupedCheckboxInput<T extends FieldValues | undefined>({
  value,
  onChange,
  ...props
}: ControlledGroupedCheckboxInputProps<T>) {
  const { error } = useFormField()

  // Ensure value is always an object and never undefined
  const safeValue = value ? value : ({} as NonNullable<T>)

  // Create a record of checked values
  const checkedValues: Record<string, boolean> = {}
  Object.keys(props.options).forEach((key) => {
    checkedValues[key] = (safeValue[key] as boolean) || false
  })

  function handleParentChange(checked: boolean) {
    onChange(
      Object.keys(props.options).reduce((acc, key) => ({ ...acc, [key]: checked }), {} as T)
    )
  }

  function handleChildChange(key: string, checked: boolean) {
    onChange({ ...safeValue, [key]: checked })
  }

  return (
    <Checkbox.Group
      {...props}
      errorMessage={error ? String(error?.message) : undefined}
      checkedValues={checkedValues}
      onParentChange={handleParentChange}
      onChildChange={handleChildChange}
    />
  )
}
